import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import DashboardHeader from "./CompanyComponents/DashboardHeaderRouter";

function RozNamacha() {
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [isUrdu, setIsUrdu] = useState(false);

  // Data states
  const [supplierBills, setSupplierBills] = useState([]);
  const [customerReceipts, setCustomerReceipts] = useState([]);
  const [recoveryData, setRecoveryData] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [ledgerParties, setLedgerParties] = useState([]);

  // Loading states
  const [loadingSupplierBills, setLoadingSupplierBills] = useState(false);
  const [loadingCustomerReceipts, setLoadingCustomerReceipts] = useState(false);
  const [loadingRecovery, setLoadingRecovery] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);

  // Filter states for Supplier Bills
  const [filterSupplierBills, setFilterSupplierBills] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    status: 'all'
  });

  // Filter states for Customer Receipts
  const [filterCustomerReceipts, setFilterCustomerReceipts] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    status: 'all'
  });

  // Filter states for Recovery Data
  const [filterRecovery, setFilterRecovery] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    status: 'all'
  });

  // Filter states for Expenses
  const [filterExpenses, setFilterExpenses] = useState({
    search: '',
    dateFrom: '',
    dateTo: '',
    category: 'all',
    status: 'all'
  });

  useEffect(() => {
    fetchCompanyData();
  }, []);

  useEffect(() => {
    if (company) {
      fetchAllData();
    }
  }, [company]);

  const handleLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    toast.success(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/company/login");
  };

  const fetchCompanyData = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.company) {
        const companyData = response.data.company;
        // Redirect CompanyDashboardPart2 users to roznamacha-p2
        if (companyData.dashboardType === 'part2') {
          navigate('/roznamacha-p2');
          return;
        }
        setCompany(companyData);
        setCompanyLanguage(companyData.language || 'en');
        setIsUrdu(companyData.language === 'ur');
      }
    } catch (error) {
      console.error("Error fetching company data:", error);
      toast.error(isUrdu ? 'کمپنی ڈیٹا لانے میں ناکام' : 'Failed to fetch company data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllData = async () => {
    // Fetch ledger parties first, then fetch other data
    await fetchLedgerParties();
    await Promise.all([
      fetchSupplierBills(),
      fetchCustomerReceipts(),
      fetchRecoveryData(),
      fetchExpenses()
    ]);
  };

  const fetchLedgerParties = async () => {
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const response = await api.get('/ledger/parties', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const parties = response.data.parties || [];
      setLedgerParties(parties);
      return parties; // Return parties for use in fetchSupplierBills
    } catch (error) {
      console.error("Error fetching ledger parties:", error);
      return [];
    }
  };

  const fetchSupplierBills = async () => {
    setLoadingSupplierBills(true);
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Get current ledger parties from state, or fetch if not available
      let parties = ledgerParties;
      if (parties.length === 0) {
        parties = await fetchLedgerParties();
      }

      const response = await api.get(`/bill/company/my-bills`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.bills) {
        const bills = response.data.bills.map(bill => {
          // Try to find matching supplier in ledger parties to get name in correct language and phone number
          let supplierName = bill.customerName || '';
          let supplierPhone = bill.customerPhone || '';

          // Try to find from ledger parties to get Urdu name and phone
          if (supplierName && parties && parties.length > 0) {
            const matchingParty = parties.find(party => {
              const partyName = party.name || '';
              const partyNameUrdu = party.nameUrdu || '';
              return party.type === 'supplier' && (
                partyName.toLowerCase() === supplierName.toLowerCase() ||
                partyNameUrdu === supplierName ||
                partyName === supplierName ||
                partyNameUrdu === supplierName ||
                supplierName.toLowerCase().includes(partyName.toLowerCase()) ||
                partyName.toLowerCase().includes(supplierName.toLowerCase())
              );
            });

            if (matchingParty) {
              // Set name based on language preference
              supplierName = isUrdu ? (matchingParty.nameUrdu || matchingParty.name) : (matchingParty.name || matchingParty.nameUrdu);
              // Get phone if not already present
              if (!supplierPhone && matchingParty.phone) {
                supplierPhone = matchingParty.phone;
              }
            }
          }

          return {
            id: bill._id,
            date: bill.date ? new Date(bill.date).toLocaleDateString('en-CA') : '',
            billNumber: bill.billNumber || bill.billNo || bill.serialNumber || bill._id?.toString().slice(-6),
            supplierName: supplierName,
            supplierPhone: supplierPhone,
            vehicleNumber: bill.vehicleNumber || '',
            totalAmount: bill.totalAmount || bill.total || 0,
            paidAmount: bill.paidAmount || 0,
            remainingAmount: (bill.totalAmount || bill.total || 0) - (bill.paidAmount || 0),
            status: bill.status || 'pending'
          };
        }).sort((a, b) => {
          // Sort by bill number/serial number first (sequence order)
          const billNumA = a.billNumber || '';
          const billNumB = b.billNumber || '';

          // Try numeric comparison first
          const numA = parseInt(billNumA);
          const numB = parseInt(billNumB);

          if (!isNaN(numA) && !isNaN(numB)) {
            // Both are numbers, compare numerically
            if (numA !== numB) return numA - numB;
          } else {
            // String comparison
            const strCompare = billNumA.localeCompare(billNumB, undefined, { numeric: true, sensitivity: 'base' });
            if (strCompare !== 0) return strCompare;
          }

          // If bill numbers are same, sort by date (oldest first)
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }

          return 0;
        });
        setSupplierBills(bills);
      }
    } catch (error) {
      console.error("Error fetching supplier bills:", error);
      toast.error(isUrdu ? 'سپلائر بلز لانے میں ناکام' : 'Failed to fetch supplier bills');
    } finally {
      setLoadingSupplierBills(false);
    }
  };

  const fetchCustomerReceipts = async () => {
    setLoadingCustomerReceipts(true);
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Get current ledger parties from state, or fetch if not available
      let parties = ledgerParties;
      if (parties.length === 0) {
        parties = await fetchLedgerParties();
      }

      const response = await api.get(`/sale/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.sales) {
        const receipts = response.data.sales.map(sale => {
          // Get customer info from sale object or ledger parties
          let customerName = '';
          let customerPhone = '';

          // First, try to get from sale object directly (if populated)
          if (sale.customerName) {
            customerName = sale.customerName;
          }
          if (sale.customerPhone) {
            customerPhone = sale.customerPhone;
          }

          // If customerPartyId exists (either as object or ID), find the party
          if (sale.customerPartyId && parties && parties.length > 0) {
            let partyId = null;

            // Handle if customerPartyId is populated (object) or just ID
            if (typeof sale.customerPartyId === 'object' && sale.customerPartyId !== null) {
              partyId = sale.customerPartyId._id || sale.customerPartyId.id;
              // If populated, we can get name directly
              if (sale.customerPartyId.name || sale.customerPartyId.nameUrdu) {
                customerName = isUrdu ? (sale.customerPartyId.nameUrdu || sale.customerPartyId.name) : (sale.customerPartyId.name || sale.customerPartyId.nameUrdu);
                customerPhone = sale.customerPartyId.phone || customerPhone;
              }
            } else {
              partyId = sale.customerPartyId;
            }

            // Find matching party in ledger parties array
            if (partyId) {
              const matchingParty = parties.find(party => {
                const partyIdStr = party._id?.toString() || party._id;
                const searchIdStr = partyId?.toString() || partyId;
                return partyIdStr === searchIdStr;
              });

              if (matchingParty && matchingParty.type === 'customer') {
                customerName = isUrdu ? (matchingParty.nameUrdu || matchingParty.name) : (matchingParty.name || matchingParty.nameUrdu);
                customerPhone = matchingParty.phone || customerPhone;
              }
            }
          }

          // If still no name/phone, try to find by name matching
          if ((!customerName || !customerPhone) && parties && parties.length > 0) {
            // Try to find by supplierName (which might be customer name in some cases)
            const searchName = sale.supplierName || sale.customerName || '';
            if (searchName) {
              const matchingParty = parties.find(party => {
                if (party.type !== 'customer') return false;
                const partyName = (party.name || '').trim();
                const partyNameUrdu = (party.nameUrdu || '').trim();
                const searchLower = searchName.toLowerCase().trim();

                return (
                  partyName.toLowerCase() === searchLower ||
                  partyNameUrdu === searchName ||
                  partyName === searchName ||
                  partyNameUrdu === searchName ||
                  searchLower.includes(partyName.toLowerCase()) ||
                  partyName.toLowerCase().includes(searchLower) ||
                  searchName.includes(partyNameUrdu) ||
                  partyNameUrdu.includes(searchName)
                );
              });

              if (matchingParty) {
                if (!customerName) {
                  customerName = isUrdu ? (matchingParty.nameUrdu || matchingParty.name) : (matchingParty.name || matchingParty.nameUrdu);
                }
                if (!customerPhone && matchingParty.phone) {
                  customerPhone = matchingParty.phone;
                }
              }
            }
          }

          return {
            id: sale._id,
            date: sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : '',
            receiptNumber: sale.receiptNumber || sale.serialNumber || sale._id?.toString().slice(-6),
            customerName: customerName,
            customerPhone: customerPhone,
            totalAmount: sale.totalAmount || sale.total || 0,
            paidAmount: sale.paidAmount || sale.amountPaid || 0,
            remainingAmount: (sale.totalAmount || sale.total || 0) - (sale.paidAmount || sale.amountPaid || 0),
            status: sale.status || 'pending'
          };
        }).sort((a, b) => {
          // Sort by receipt number/serial number first (sequence order)
          const receiptNumA = a.receiptNumber || '';
          const receiptNumB = b.receiptNumber || '';

          // Try numeric comparison first
          const numA = parseInt(receiptNumA);
          const numB = parseInt(receiptNumB);

          if (!isNaN(numA) && !isNaN(numB)) {
            // Both are numbers, compare numerically
            if (numA !== numB) return numA - numB;
          } else {
            // String comparison
            const strCompare = receiptNumA.localeCompare(receiptNumB, undefined, { numeric: true, sensitivity: 'base' });
            if (strCompare !== 0) return strCompare;
          }

          // If receipt numbers are same, sort by date (oldest first)
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }

          return 0;
        });
        setCustomerReceipts(receipts);
      }
    } catch (error) {
      console.error("Error fetching customer receipts:", error);
      toast.error(isUrdu ? 'کسٹمر رسید لانے میں ناکام' : 'Failed to fetch customer receipts');
    } finally {
      setLoadingCustomerReceipts(false);
    }
  };

  const fetchRecoveryData = async () => {
    setLoadingRecovery(true);
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      // Get current ledger parties from state, or fetch if not available
      let parties = ledgerParties;
      if (parties.length === 0) {
        parties = await fetchLedgerParties();
      }

      // Fetch recovery data
      const response = await api.get(`/recovery/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.recoveries) {
        // Get unique customer party IDs from recoveries
        const uniquePartyIds = [...new Set(
          response.data.recoveries
            .map(rec => rec.customerPartyId)
            .filter(id => id)
        )];

        // Fetch transactions for each party
        const partyTransactionsMap = {};
        for (const partyId of uniquePartyIds) {
          try {
            const partyTxResponse = await api.get(`/ledger/parties/${partyId}/transactions`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (partyTxResponse.data && partyTxResponse.data.transactions) {
              partyTransactionsMap[partyId] = partyTxResponse.data.transactions;
            }
          } catch (error) {
            console.error(`Error fetching transactions for party ${partyId}:`, error);
            partyTransactionsMap[partyId] = [];
          }
        }

        const recoveries = response.data.recoveries.map(rec => {
          const recoveryAmount = rec.recoveryAmount || rec.amount || 0;
          const customerPartyId = rec.customerPartyId;

          // Get customer name based on language preference
          let customerName = rec.partyName || rec.customerName || rec.customerNameUrdu || '';

          // Try to find the party in ledger parties to get correct language name
          if (customerPartyId && parties && parties.length > 0) {
            const matchingParty = parties.find(party => {
              const partyIdStr = party._id?.toString() || party._id;
              const searchIdStr = customerPartyId?.toString() || customerPartyId;
              return partyIdStr === searchIdStr;
            });

            if (matchingParty) {
              customerName = isUrdu ? (matchingParty.nameUrdu || matchingParty.name) : (matchingParty.name || matchingParty.nameUrdu);
            }
          }

          // If still no name, try to match by existing name
          if ((!customerName || customerName === rec.partyName) && parties && parties.length > 0) {
            const searchName = rec.partyName || rec.customerName || rec.customerNameUrdu || '';
            if (searchName) {
              const matchingParty = parties.find(party => {
                const partyName = (party.name || '').trim();
                const partyNameUrdu = (party.nameUrdu || '').trim();
                const searchLower = searchName.toLowerCase().trim();

                return (
                  partyName.toLowerCase() === searchLower ||
                  partyNameUrdu === searchName ||
                  partyName === searchName ||
                  partyNameUrdu === searchName ||
                  searchLower.includes(partyName.toLowerCase()) ||
                  partyName.toLowerCase().includes(searchLower) ||
                  searchName.includes(partyNameUrdu) ||
                  partyNameUrdu.includes(searchName)
                );
              });

              if (matchingParty) {
                customerName = isUrdu ? (matchingParty.nameUrdu || matchingParty.name) : (matchingParty.name || matchingParty.nameUrdu);
              }
            }
          }

          // Get transactions for this party
          const partyTransactions = customerPartyId ? (partyTransactionsMap[customerPartyId] || []) : [];

          // Find the ledger transaction for this recovery
          const recoveryTx = partyTransactions.find(tx =>
            tx.recoveryId && tx.recoveryId.toString() === rec._id.toString()
          );

          // Calculate previous balance (balance before recovery) and remaining balance (balance after recovery)
          let previousBalance = 0;
          let remainingBalance = 0;

          if (recoveryTx && recoveryTx.balanceAfter !== undefined && recoveryTx.balanceAfter !== null) {
            // If we have balanceAfter from ledger transaction, calculate previous balance
            // For customers: recovery (credit) reduces balance, so previous = balanceAfter + recoveryAmount
            previousBalance = recoveryTx.balanceAfter + recoveryAmount;
            remainingBalance = recoveryTx.balanceAfter;
          } else if (partyTransactions.length > 0) {
            // Sort transactions by date
            const sortedTransactions = partyTransactions.sort((a, b) => {
              const dateA = new Date(a.date || a.createdAt);
              const dateB = new Date(b.date || b.createdAt);
              if (dateA.getTime() !== dateB.getTime()) {
                return dateA.getTime() - dateB.getTime();
              }
              return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
            });

            const recoveryTxIndex = sortedTransactions.findIndex(tx =>
              tx.recoveryId && tx.recoveryId.toString() === rec._id.toString()
            );

            if (recoveryTxIndex > 0 && sortedTransactions[recoveryTxIndex - 1].balanceAfter !== undefined) {
              // Previous balance is the balanceAfter of the transaction before this recovery
              previousBalance = sortedTransactions[recoveryTxIndex - 1].balanceAfter || 0;
              // Remaining balance = previous balance - recovery amount (for customers, recovery reduces balance)
              remainingBalance = previousBalance - recoveryAmount;
            } else if (recoveryTxIndex === 0) {
              // This is the first transaction, previous balance would be opening balance
              // Get opening balance from party data or calculate from first transaction
              if (sortedTransactions[0].balanceAfter !== undefined) {
                // Calculate opening balance: balanceAfter - transaction amount (if debit) or + transaction amount (if credit)
                const firstTx = sortedTransactions[0];
                if (firstTx.type === 'debit') {
                  previousBalance = firstTx.balanceAfter - firstTx.amount;
                } else if (firstTx.type === 'credit') {
                  previousBalance = firstTx.balanceAfter + firstTx.amount;
                } else {
                  previousBalance = firstTx.balanceAfter - recoveryAmount;
                }
                remainingBalance = firstTx.balanceAfter;
              } else {
                previousBalance = 0;
                remainingBalance = 0 - recoveryAmount;
              }
            } else if (recoveryTxIndex === -1 && recoveryAmount > 0) {
              // Recovery transaction not found in ledger, but we have recovery amount
              // Try to estimate from last transaction before recovery date
              const recoveryDate = new Date(rec.date || rec.createdAt);
              const transactionsBeforeRecovery = sortedTransactions.filter(tx => {
                const txDate = new Date(tx.date || tx.createdAt);
                return txDate < recoveryDate;
              });

              if (transactionsBeforeRecovery.length > 0) {
                const lastTx = transactionsBeforeRecovery[transactionsBeforeRecovery.length - 1];
                previousBalance = lastTx.balanceAfter || 0;
                remainingBalance = previousBalance - recoveryAmount;
              }
            }
          }

          return {
            id: rec._id,
            date: rec.date ? new Date(rec.date).toLocaleDateString('en-CA') : '',
            recoveryNumber: rec.recoveryNumber || rec.serialNumber || rec._id?.toString().slice(-6),
            partyName: customerName,
            recoveryAmount: recoveryAmount,
            previousBalance: previousBalance,
            remainingBalance: remainingBalance,
            status: rec.status || 'completed'
          };
        }).sort((a, b) => {
          // Sort by recovery number/serial number first (sequence order)
          const recoveryNumA = a.recoveryNumber || '';
          const recoveryNumB = b.recoveryNumber || '';

          // Try numeric comparison first
          const numA = parseInt(recoveryNumA);
          const numB = parseInt(recoveryNumB);

          if (!isNaN(numA) && !isNaN(numB)) {
            // Both are numbers, compare numerically
            if (numA !== numB) return numA - numB;
          } else {
            // String comparison
            const strCompare = recoveryNumA.localeCompare(recoveryNumB, undefined, { numeric: true, sensitivity: 'base' });
            if (strCompare !== 0) return strCompare;
          }

          // If recovery numbers are same, sort by date (oldest first)
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }

          return 0;
        });
        setRecoveryData(recoveries);
      } else {
        console.log("No recoveries found in response");
        setRecoveryData([]);
      }
    } catch (error) {
      console.error("Error fetching recovery data:", error);
      console.error("Error response:", error.response?.data);
      toast.error(isUrdu ? 'ریکوری ڈیٹا لانے میں ناکام' : 'Failed to fetch recovery data');
      setRecoveryData([]);
    } finally {
      setLoadingRecovery(false);
    }
  };

  const fetchExpenses = async () => {
    setLoadingExpenses(true);
    try {
      const token = localStorage.getItem("companyToken");
      if (!token) return;

      const response = await api.get(`/expense-ledger/company/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.expenses) {
        const expensesData = response.data.expenses.map(exp => ({
          id: exp._id,
          date: exp.date ? new Date(exp.date).toLocaleDateString('en-CA') : '',
          expenseNumber: exp.expenseNumber || exp.serialNumber || exp._id?.toString().slice(-6),
          category: exp.category || '',
          description: exp.description || exp.descriptionUrdu || '',
          amount: exp.amount || 0,
          paymentMethod: exp.paymentMethod || '',
          status: exp.status || 'completed'
        })).sort((a, b) => {
          // Sort by expense number/serial number first (sequence order)
          const expenseNumA = a.expenseNumber || '';
          const expenseNumB = b.expenseNumber || '';

          // Try numeric comparison first
          const numA = parseInt(expenseNumA);
          const numB = parseInt(expenseNumB);

          if (!isNaN(numA) && !isNaN(numB)) {
            // Both are numbers, compare numerically
            if (numA !== numB) return numA - numB;
          } else {
            // String comparison
            const strCompare = expenseNumA.localeCompare(expenseNumB, undefined, { numeric: true, sensitivity: 'base' });
            if (strCompare !== 0) return strCompare;
          }

          // If expense numbers are same, sort by date (oldest first)
          if (a.date && b.date) {
            return a.date.localeCompare(b.date);
          }

          return 0;
        });
        setExpenses(expensesData);
      }
    } catch (error) {
      console.error("Error fetching expenses:", error);
      toast.error(isUrdu ? 'خراجات لانے میں ناکام' : 'Failed to fetch expenses');
    } finally {
      setLoadingExpenses(false);
    }
  };

  // Filter functions
  const filterSupplierBillsData = (bills) => {
    return bills.filter(bill => {
      const matchesSearch = !filterSupplierBills.search ||
        bill.supplierName.toLowerCase().includes(filterSupplierBills.search.toLowerCase()) ||
        bill.billNumber.toLowerCase().includes(filterSupplierBills.search.toLowerCase()) ||
        (bill.supplierPhone && bill.supplierPhone.includes(filterSupplierBills.search));

      const matchesDateFrom = !filterSupplierBills.dateFrom || bill.date >= filterSupplierBills.dateFrom;
      const matchesDateTo = !filterSupplierBills.dateTo || bill.date <= filterSupplierBills.dateTo;
      const matchesStatus = filterSupplierBills.status === 'all' || bill.status === filterSupplierBills.status;

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesStatus;
    });
  };

  const filterCustomerReceiptsData = (receipts) => {
    return receipts.filter(receipt => {
      const matchesSearch = !filterCustomerReceipts.search ||
        receipt.customerName.toLowerCase().includes(filterCustomerReceipts.search.toLowerCase()) ||
        receipt.receiptNumber.toLowerCase().includes(filterCustomerReceipts.search.toLowerCase()) ||
        (receipt.customerPhone && receipt.customerPhone.includes(filterCustomerReceipts.search));

      const matchesDateFrom = !filterCustomerReceipts.dateFrom || receipt.date >= filterCustomerReceipts.dateFrom;
      const matchesDateTo = !filterCustomerReceipts.dateTo || receipt.date <= filterCustomerReceipts.dateTo;
      const matchesStatus = filterCustomerReceipts.status === 'all' || receipt.status === filterCustomerReceipts.status;

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesStatus;
    });
  };

  const filterRecoveryData = (recoveries) => {
    return recoveries.filter(rec => {
      const matchesSearch = !filterRecovery.search ||
        rec.partyName.toLowerCase().includes(filterRecovery.search.toLowerCase()) ||
        rec.recoveryNumber.toLowerCase().includes(filterRecovery.search.toLowerCase()) ||
        (rec.partyPhone && rec.partyPhone.includes(filterRecovery.search));

      const matchesDateFrom = !filterRecovery.dateFrom || rec.date >= filterRecovery.dateFrom;
      const matchesDateTo = !filterRecovery.dateTo || rec.date <= filterRecovery.dateTo;
      const matchesStatus = filterRecovery.status === 'all' || rec.status === filterRecovery.status;

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesStatus;
    });
  };

  const filterExpensesData = (expensesList) => {
    return expensesList.filter(exp => {
      const matchesSearch = !filterExpenses.search ||
        exp.description.toLowerCase().includes(filterExpenses.search.toLowerCase()) ||
        exp.expenseNumber.toLowerCase().includes(filterExpenses.search.toLowerCase()) ||
        (exp.category && exp.category.toLowerCase().includes(filterExpenses.search.toLowerCase()));

      const matchesDateFrom = !filterExpenses.dateFrom || exp.date >= filterExpenses.dateFrom;
      const matchesDateTo = !filterExpenses.dateTo || exp.date <= filterExpenses.dateTo;
      const matchesCategory = filterExpenses.category === 'all' || exp.category === filterExpenses.category;
      const matchesStatus = filterExpenses.status === 'all' || exp.status === filterExpenses.status;

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesCategory && matchesStatus;
    });
  };

  // Get unique categories for expenses filter
  const getUniqueCategories = () => {
    return [...new Set(expenses.map(exp => exp.category).filter(Boolean))];
  };

  // Translate status to Urdu
  const translateStatus = (status) => {
    if (!isUrdu) return status;
    const statusMap = {
      'completed': 'مکمل',
      'pending': 'زیر التواء',
      'paid': 'ادا شدہ',
      'partial': 'جزوی',
      'all': 'تمام'
    };
    return statusMap[status] || status;
  };

  // Translate description to Urdu
  const translateDescription = (description) => {
    if (!isUrdu || !description) return description;

    let translatedDesc = String(description);

    // Handle EXPENSE_FIELD:FieldName:Number format
    translatedDesc = translatedDesc.replace(/EXPENSE_FIELD:([^:]+):(\d+)/gi, (match, fieldName, number) => {
      const fieldTranslations = {
        'Cash': 'نقدی اخراجات',
        'Rent': 'کرایہ اخراجات',
        'Commission': 'کمیشن اخراجات',
        'Labor': 'مزدوری اخراجات',
        'Wages': 'مزدوری اخراجات',
        'Miscellaneous': 'مشیانہ اخراجات',
        'Mashiana': 'مشیانہ اخراجات',
        'Market Fees': 'مارکیٹ فیس اخراجات'
      };
      const translatedField = fieldTranslations[fieldName.trim()] || fieldName;
      return `${translatedField}:${number}`;
    });

    // Handle EXPENSE_FIELD:FieldName format (without number)
    translatedDesc = translatedDesc.replace(/EXPENSE_FIELD:([^:\s]+)/gi, (match, fieldName) => {
      const fieldTranslations = {
        'Cash': 'نقدی اخراجات',
        'Rent': 'کرایہ اخراجات',
        'Commission': 'کمیشن اخراجات',
        'Labor': 'مزدوری اخراجات',
        'Wages': 'مزدوری اخراجات',
        'Miscellaneous': 'مشیانہ اخراجات',
        'Mashiana': 'مشیانہ اخراجات',
        'Market Fees': 'مارکیٹ فیس اخراجات'
      };
      return fieldTranslations[fieldName.trim()] || fieldName;
    });

    // Common English phrases to Urdu translations (longer patterns first)
    const translations = {
      'Cash Expense Field - Bill': 'نقدی اخراجات - بل',
      'Rent Expense Field - Bill': 'کرایہ اخراجات - بل',
      'Commission Expense Field - Bill': 'کمیشن اخراجات - بل',
      'Labor Expense Field - Bill': 'مزدوری اخراجات - بل',
      'Wages Expense Field - Bill': 'مزدوری اخراجات - بل',
      'Miscellaneous Expense Field - Bill': 'مشیانہ اخراجات - بل',
      'Mashiana Expense Field - Bill': 'مشیانہ اخراجات - بل',
      'Market Fees Expense Field - Bill': 'مارکیٹ فیس اخراجات - بل',
      'Cash Expense Field': 'نقدی اخراجات',
      'Rent Expense Field': 'کرایہ اخراجات',
      'Commission Expense Field': 'کمیشن اخراجات',
      'Labor Expense Field': 'مزدوری اخراجات',
      'Wages Expense Field': 'مزدوری اخراجات',
      'Miscellaneous Expense Field': 'مشیانہ اخراجات',
      'Mashiana Expense Field': 'مشیانہ اخراجات',
      'Market Fees Expense Field': 'مارکیٹ فیس اخراجات',
      'Expense Field': 'خراجات',
      'Bill': 'بل'
    };

    // Replace common patterns (longer patterns first)
    Object.keys(translations).sort((a, b) => b.length - a.length).forEach(key => {
      const regex = new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      translatedDesc = translatedDesc.replace(regex, translations[key]);
    });

    return translatedDesc;
  };

  // Translate category to Urdu
  const translateCategory = (category) => {
    if (!isUrdu || !category) return category;

    const categoryTranslations = {
      'Cash': 'نقدی',
      'Rent': 'کرایہ',
      'Commission': 'کمیشن',
      'Labor': 'مزدوری',
      'Wages': 'مزدوری',
      'Miscellaneous': 'مشیانہ',
      'Mashiana': 'مشیانہ',
      'Market Fees': 'مارکیٹ فیس'
    };

    return categoryTranslations[category] || category;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className={`text-lg font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : ''}`}>
            {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Company Header - Hidden */}
      {/* {company && (
        <DashboardHeader 
          company={company} 
          companyLanguage={companyLanguage} 
          onLogout={handleLogout} 
        />
      )} */}

      <Helmet>
        <title>{isUrdu ? 'روزنامچہ' : 'Roz Namacha'}</title>
        <style>{`
          @keyframes fadeInDown {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-30px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          @keyframes float {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
          .animate-fadeInDown {
            animation: fadeInDown 0.6s ease-out;
          }
          .animate-fadeInUp {
            animation: fadeInUp 0.6s ease-out;
          }
          .animate-slideInLeft {
            animation: slideInLeft 0.6s ease-out;
          }
          .animate-float {
            animation: float 3s ease-in-out infinite;
          }
          .animate-pulse-slow {
            animation: pulse 3s ease-in-out infinite;
          }
        `}</style>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 pb-8 px-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse-slow"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Modern Header with Dashboard Button */}
          <div className="bg-gradient-to-r from-white to-purple-50/30 rounded-xl shadow-xl border border-purple-100 mb-6 mt-4 overflow-hidden" dir={isUrdu ? 'rtl' : 'ltr'}>
            <div className={`flex justify-between items-center py-6 px-5 ${isUrdu ? 'flex-row-reverse' : ''}`} style={{ minHeight: '80px' }}>
              {/* Dashboard Button - Left side in Urdu, Right side in English */}
              <button
                onClick={() => navigate('/company/dashboard')}
                className={`group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold px-6 py-3 rounded-xl transition-all transform hover:scale-105 hover:shadow-xl flex items-center gap-2 overflow-hidden animate-slideInLeft ${isUrdu ? 'font-urdu flex-row-reverse' : ''}`}
                style={{ animationDelay: '0.2s' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700"></div>
                <svg className={`w-5 h-5 relative z-10 transform group-hover:scale-110 transition-transform duration-300 ${isUrdu ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                <span className="relative z-10">{isUrdu ? 'ڈیش بورڈ' : 'Dashboard'}</span>
              </button>

              {/* Page Heading - Right side in Urdu, Left side in English */}
              <div className={`flex items-center gap-4 ${isUrdu ? 'flex-row-reverse' : ''} animate-slideInLeft`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-purple-400 rounded-xl blur-lg opacity-30 animate-pulse-slow"></div>
                  <div className="relative bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-3 shadow-lg animate-float">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
                <div className={isUrdu ? 'text-right' : 'text-left'}>
                  <h1 className={`text-3xl font-bold bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", lineHeight: '1.6' } : { lineHeight: '1.6' }}>
                    {isUrdu ? 'روزنامہ' : 'Roznama'}
                  </h1>
                  <p className={`text-gray-600 text-sm font-medium mt-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'تمام ریکارڈز کا جامع جائزہ' : 'Comprehensive Records Overview'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Supplier Bills Section */}
          <div className="mb-8 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border-2 border-blue-200 hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 transform">
              <div className={`bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 px-6 py-4 relative overflow-hidden ${isUrdu ? 'text-right' : 'text-left'}`}>
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
                <div className="relative z-10">
                  <h2 className={`text-2xl font-bold text-white flex items-center ${isUrdu ? 'font-urdu flex-row-reverse' : ''}`}>
                    <span className={isUrdu ? 'ml-3' : 'mr-3'}>📋</span>
                    {isUrdu ? 'سپلائر بلز' : 'Supplier Bills'}
                    <span className={`text-sm font-normal bg-white/20 px-3 py-1 rounded-full ${isUrdu ? 'mr-4' : 'ml-4'}`}>
                      {filterSupplierBillsData(supplierBills).length} / {supplierBills.length}
                    </span>
                  </h2>
                </div>
              </div>

              {/* Filter Section for Supplier Bills */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تلاش' : 'Search'}
                    </label>
                    <input
                      type="text"
                      value={filterSupplierBills.search}
                      onChange={(e) => setFilterSupplierBills({ ...filterSupplierBills, search: e.target.value })}
                      placeholder={isUrdu ? 'نام، بل نمبر، یا فون' : 'Name, Bill Number, or Phone'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ سے' : 'Date From'}
                    </label>
                    <input
                      type="date"
                      value={filterSupplierBills.dateFrom}
                      onChange={(e) => setFilterSupplierBills({ ...filterSupplierBills, dateFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ تک' : 'Date To'}
                    </label>
                    <input
                      type="date"
                      value={filterSupplierBills.dateTo}
                      onChange={(e) => setFilterSupplierBills({ ...filterSupplierBills, dateTo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'حالت' : 'Status'}
                    </label>
                    <select
                      value={filterSupplierBills.status}
                      onChange={(e) => setFilterSupplierBills({ ...filterSupplierBills, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">{isUrdu ? 'تمام' : 'All'}</option>
                      <option value="pending">{isUrdu ? 'زیر التواء' : 'Pending'}</option>
                      <option value="partial">{isUrdu ? 'جزوی' : 'Partial'}</option>
                      <option value="paid">{isUrdu ? 'ادا شدہ' : 'Paid'}</option>
                      <option value="completed">{isUrdu ? 'مکمل' : 'Completed'}</option>
                    </select>
                  </div>
                </div>
                <div className={`mt-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                  <button
                    onClick={() => setFilterSupplierBills({ search: '', dateFrom: '', dateTo: '', status: 'all' })}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {isUrdu ? 'فلٹرز صاف کریں' : 'Clear Filters'}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loadingSupplierBills ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
                  </div>
                ) : supplierBills.length > 0 ? (
                  <table className={`min-w-full divide-y divide-gray-200 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'بل نمبر' : 'Bill Number'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'سپلائر' : 'Supplier'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'فون' : 'Phone'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'کل رقم' : 'Total Amount'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'ادا شده' : 'Paid'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'باقی' : 'Remaining'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'حالت' : 'Status'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filterSupplierBillsData(supplierBills).map((bill, index) => (
                        <tr
                          key={bill.id}
                          className="hover:bg-blue-50 transition-colors duration-150"
                          style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both` }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                            {bill.date}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-blue-600 text-right">
                            {bill.billNumber}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                            {bill.supplierName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                            {bill.supplierPhone || ''}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {parseFloat(bill.totalAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                            {parseFloat(bill.paidAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-red-600 text-right">
                            {parseFloat(bill.remainingAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isUrdu ? 'font-urdu' : ''} ${bill.status === 'paid' || bill.status === 'completed' ? 'bg-red-100 text-red-800' :
                                bill.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                              }`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {translateStatus(bill.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : filterSupplierBillsData(supplierBills).length === 0 && supplierBills.length > 0 ? (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'فلٹرز کے مطابق کوئی نتیجہ نہیں ملا' : 'No results match the filters'}</p>
                  </div>
                ) : (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'کوئی سپلائر بل نہیں ملا' : 'No supplier bills found'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Customer Receipts Section */}
          <div className="mb-8 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border-2 border-green-200 hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 transform">
              <div className={`bg-gradient-to-r from-green-600 via-green-700 to-green-800 px-6 py-4 relative overflow-hidden ${isUrdu ? 'text-right' : 'text-left'}`}>
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
                <div className="relative z-10">
                  <h2 className={`text-2xl font-bold text-white flex items-center ${isUrdu ? 'font-urdu flex-row-reverse' : ''}`}>
                    <span className={isUrdu ? 'ml-3' : 'mr-3'}>🧾</span>
                    {isUrdu ? 'کسٹمر رسید' : 'Customer Receipts'}
                    <span className={`text-sm font-normal bg-white/20 px-3 py-1 rounded-full ${isUrdu ? 'mr-4' : 'ml-4'}`}>
                      {filterCustomerReceiptsData(customerReceipts).length} / {customerReceipts.length}
                    </span>
                  </h2>
                </div>
              </div>

              {/* Filter Section for Customer Receipts */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تلاش' : 'Search'}
                    </label>
                    <input
                      type="text"
                      value={filterCustomerReceipts.search}
                      onChange={(e) => setFilterCustomerReceipts({ ...filterCustomerReceipts, search: e.target.value })}
                      placeholder={isUrdu ? 'نام، رسید نمبر، یا فون' : 'Name, Receipt Number, or Phone'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ سے' : 'Date From'}
                    </label>
                    <input
                      type="date"
                      value={filterCustomerReceipts.dateFrom}
                      onChange={(e) => setFilterCustomerReceipts({ ...filterCustomerReceipts, dateFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ تک' : 'Date To'}
                    </label>
                    <input
                      type="date"
                      value={filterCustomerReceipts.dateTo}
                      onChange={(e) => setFilterCustomerReceipts({ ...filterCustomerReceipts, dateTo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'حالت' : 'Status'}
                    </label>
                    <select
                      value={filterCustomerReceipts.status}
                      onChange={(e) => setFilterCustomerReceipts({ ...filterCustomerReceipts, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="all">{isUrdu ? 'تمام' : 'All'}</option>
                      <option value="pending">{isUrdu ? 'زیر التواء' : 'Pending'}</option>
                      <option value="partial">{isUrdu ? 'جزوی' : 'Partial'}</option>
                      <option value="paid">{isUrdu ? 'ادا شدہ' : 'Paid'}</option>
                      <option value="completed">{isUrdu ? 'مکمل' : 'Completed'}</option>
                    </select>
                  </div>
                </div>
                <div className={`mt-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                  <button
                    onClick={() => setFilterCustomerReceipts({ search: '', dateFrom: '', dateTo: '', status: 'all' })}
                    className="text-sm text-green-600 hover:text-green-800 font-medium"
                  >
                    {isUrdu ? 'فلٹرز صاف کریں' : 'Clear Filters'}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loadingCustomerReceipts ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-green-600 mx-auto"></div>
                  </div>
                ) : customerReceipts.length > 0 ? (
                  <table className={`min-w-full divide-y divide-gray-200 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'رسید نمبر' : 'Receipt Number'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'کسٹمر' : 'Customer'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'فون' : 'Phone'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'کل رقم' : 'Total Amount'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'ادا شده' : 'Paid'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'باقی' : 'Remaining'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'حالت' : 'Status'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filterCustomerReceiptsData(customerReceipts).map((receipt, index) => (
                        <tr
                          key={receipt.id}
                          className="hover:bg-green-50 transition-colors duration-150"
                          style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both` }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                            {receipt.date}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                            {receipt.receiptNumber}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                            {receipt.customerName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right">
                            {receipt.customerPhone || ''}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {parseFloat(receipt.totalAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                            {parseFloat(receipt.paidAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-red-600 text-right">
                            {parseFloat(receipt.remainingAmount).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isUrdu ? 'font-urdu' : ''} ${receipt.status === 'paid' || receipt.status === 'completed' ? 'bg-green-100 text-green-800' :
                                receipt.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                              }`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {translateStatus(receipt.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : filterCustomerReceiptsData(customerReceipts).length === 0 && customerReceipts.length > 0 ? (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'فلٹرز کے مطابق کوئی نتیجہ نہیں ملا' : 'No results match the filters'}</p>
                  </div>
                ) : (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'کوئی کسٹمر رسید نہیں ملی' : 'No customer receipts found'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recovery Data Section */}
          <div className="mb-8 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border-2 border-purple-200 hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 transform">
              <div className={`bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 px-6 py-4 relative overflow-hidden ${isUrdu ? 'text-right' : 'text-left'}`}>
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
                <div className="relative z-10">
                  <h2 className={`text-2xl font-bold text-white flex items-center ${isUrdu ? 'font-urdu flex-row-reverse' : ''}`}>
                    <span className={isUrdu ? 'ml-3' : 'mr-3'}>💰</span>
                    {isUrdu ? 'ریکوری ڈیٹا' : 'Recovery Data'}
                    <span className={`text-sm font-normal bg-white/20 px-3 py-1 rounded-full ${isUrdu ? 'mr-4' : 'ml-4'}`}>
                      {filterRecoveryData(recoveryData).length} / {recoveryData.length}
                    </span>
                  </h2>
                </div>
              </div>

              {/* Filter Section for Recovery Data */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className={`grid grid-cols-1 md:grid-cols-4 gap-4 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تلاش' : 'Search'}
                    </label>
                    <input
                      type="text"
                      value={filterRecovery.search}
                      onChange={(e) => setFilterRecovery({ ...filterRecovery, search: e.target.value })}
                      placeholder={isUrdu ? 'پارٹی نام، نمبر، یا فون' : 'Party Name, Number, or Phone'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ سے' : 'Date From'}
                    </label>
                    <input
                      type="date"
                      value={filterRecovery.dateFrom}
                      onChange={(e) => setFilterRecovery({ ...filterRecovery, dateFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ تک' : 'Date To'}
                    </label>
                    <input
                      type="date"
                      value={filterRecovery.dateTo}
                      onChange={(e) => setFilterRecovery({ ...filterRecovery, dateTo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'حالت' : 'Status'}
                    </label>
                    <select
                      value={filterRecovery.status}
                      onChange={(e) => setFilterRecovery({ ...filterRecovery, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="all">{isUrdu ? 'تمام' : 'All'}</option>
                      <option value="completed">{isUrdu ? 'مکمل' : 'Completed'}</option>
                      <option value="pending">{isUrdu ? 'زیر التواء' : 'Pending'}</option>
                    </select>
                  </div>
                </div>
                <div className={`mt-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                  <button
                    onClick={() => setFilterRecovery({ search: '', dateFrom: '', dateTo: '', status: 'all' })}
                    className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                  >
                    {isUrdu ? 'فلٹرز صاف کریں' : 'Clear Filters'}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {loadingRecovery ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-600 mx-auto"></div>
                  </div>
                ) : recoveryData.length > 0 ? (
                  <table className={`min-w-full divide-y divide-gray-200 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'ریکوری نمبر' : 'Recovery Number'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'پارٹی' : 'Party'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'کل رقم' : 'Total Amount'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'ادا شده' : 'Paid'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'باقی' : 'Remaining'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'حالت' : 'Status'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filterRecoveryData(recoveryData).map((rec, index) => (
                        <tr
                          key={rec.id}
                          className="hover:bg-purple-50 transition-colors duration-150"
                          style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both` }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                            {rec.date}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-purple-600 text-right">
                            {rec.recoveryNumber}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                            {rec.partyName}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900 text-right">
                            {parseFloat(rec.previousBalance || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-green-600 text-right">
                            {parseFloat(rec.recoveryAmount || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-red-600 text-right">
                            {parseFloat(rec.remainingBalance || 0).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isUrdu ? 'font-urdu' : ''} ${rec.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {translateStatus(rec.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : filterRecoveryData(recoveryData).length === 0 && recoveryData.length > 0 ? (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'فلٹرز کے مطابق کوئی نتیجہ نہیں ملا' : 'No results match the filters'}</p>
                  </div>
                ) : (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'کوئی ریکوری ڈیٹا نہیں ملا' : 'No recovery data found'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Expenses Section */}
          <div className="mb-8 animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border-2 border-orange-200 hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 transform">
              <div className={`bg-gradient-to-r from-orange-600 via-orange-700 to-orange-800 px-6 py-4 relative overflow-hidden ${isUrdu ? 'text-right' : 'text-left'}`}>
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                </div>
                <div className="relative z-10">
                  <h2 className={`text-2xl font-bold text-white flex items-center ${isUrdu ? 'font-urdu flex-row-reverse' : ''}`}>
                    <span className={isUrdu ? 'ml-3' : 'mr-3'}>💸</span>
                    {isUrdu ? 'خراجات' : 'Expenses'}
                    <span className={`text-sm font-normal bg-white/20 px-3 py-1 rounded-full ${isUrdu ? 'mr-4' : 'ml-4'}`}>
                      {filterExpensesData(expenses).length} / {expenses.length}
                    </span>
                  </h2>
                </div>
              </div>

              {/* Filter Section for Expenses */}
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className={`grid grid-cols-1 md:grid-cols-5 gap-4 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تلاش' : 'Search'}
                    </label>
                    <input
                      type="text"
                      value={filterExpenses.search}
                      onChange={(e) => setFilterExpenses({ ...filterExpenses, search: e.target.value })}
                      placeholder={isUrdu ? 'تفصیل، نمبر، یا زمرہ' : 'Description, Number, or Category'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ سے' : 'Date From'}
                    </label>
                    <input
                      type="date"
                      value={filterExpenses.dateFrom}
                      onChange={(e) => setFilterExpenses({ ...filterExpenses, dateFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'تاریخ تک' : 'Date To'}
                    </label>
                    <input
                      type="date"
                      value={filterExpenses.dateTo}
                      onChange={(e) => setFilterExpenses({ ...filterExpenses, dateTo: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'زمرہ' : 'Category'}
                    </label>
                    <select
                      value={filterExpenses.category}
                      onChange={(e) => setFilterExpenses({ ...filterExpenses, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="all">{isUrdu ? 'تمام' : 'All'}</option>
                      {getUniqueCategories().map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                      {isUrdu ? 'حالت' : 'Status'}
                    </label>
                    <select
                      value={filterExpenses.status}
                      onChange={(e) => setFilterExpenses({ ...filterExpenses, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="all">{isUrdu ? 'تمام' : 'All'}</option>
                      <option value="completed">{isUrdu ? 'مکمل' : 'Completed'}</option>
                      <option value="pending">{isUrdu ? 'زیر التواء' : 'Pending'}</option>
                    </select>
                  </div>
                </div>
                <div className={`mt-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                  <button
                    onClick={() => setFilterExpenses({ search: '', dateFrom: '', dateTo: '', category: 'all', status: 'all' })}
                    className="text-sm text-orange-600 hover:text-orange-800 font-medium"
                  >
                    {isUrdu ? 'فلٹرز صاف کریں' : 'Clear Filters'}
                  </button>
                </div>
              </div>

              {/* Category-wise Summary */}
              {!loadingExpenses && expenses.length > 0 && (() => {
                // Calculate category-wise totals
                const categoryTotals = {};
                filterExpensesData(expenses).forEach(exp => {
                  const category = exp.category || (isUrdu ? 'بغیر زمرہ' : 'Uncategorized');
                  if (!categoryTotals[category]) {
                    categoryTotals[category] = 0;
                  }
                  categoryTotals[category] += parseFloat(exp.amount || 0);
                });

                const sortedCategories = Object.entries(categoryTotals).sort((a, b) => {
                  // Sort by category name
                  return a[0].localeCompare(b[0]);
                });

                return (
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 border-b-2 border-orange-200 p-6">
                    <h3 className={`text-xl font-bold mb-4 text-orange-800 ${isUrdu ? 'font-urdu text-right' : 'text-left'}`}>
                      {isUrdu ? 'تفصیل اخراجات' : 'Expense Details Summary'}
                    </h3>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                      {sortedCategories.map(([category, total], index) => (
                        <div
                          key={category}
                          className={`bg-white rounded-lg p-4 border-2 border-orange-200 shadow-md hover:shadow-lg transition-all duration-200 ${index === 0 ? 'bg-gray-50' : ''}`}
                        >
                          <div className={`text-sm font-semibold text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {translateCategory(category)}
                          </div>
                          <div className="text-lg font-bold text-orange-600">
                            {total.toFixed(2)}
                          </div>
                        </div>
                      ))}
                      {sortedCategories.length > 0 && (
                        <div className="bg-gradient-to-br from-orange-600 to-red-600 rounded-lg p-4 border-2 border-orange-400 shadow-lg">
                          <div className={`text-sm font-semibold text-white mb-2 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'کل خراجات' : 'Total Expenses'}
                          </div>
                          <div className="text-xl font-bold text-white">
                            {Object.values(categoryTotals).reduce((sum, val) => sum + val, 0).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="overflow-x-auto">
                {loadingExpenses ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-600 mx-auto"></div>
                  </div>
                ) : expenses.length > 0 ? (
                  <table className={`min-w-full divide-y divide-gray-200 ${isUrdu ? 'text-right' : 'text-left'}`} dir={isUrdu ? 'rtl' : 'ltr'}>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'تاریخ' : 'Date'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'خرچ نمبر' : 'Expense Number'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'زمرہ' : 'Category'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'تفصیل' : 'Description'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'رقم' : 'Amount'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'ادائیگی کا طریقہ' : 'Payment Method'}
                        </th>
                        <th className={`px-4 py-3 text-right text-xs font-bold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`}>
                          {isUrdu ? 'حالت' : 'Status'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filterExpensesData(expenses).map((expense, index) => (
                        <tr
                          key={expense.id}
                          className="hover:bg-orange-50 transition-colors duration-150"
                          style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both` }}
                        >
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                            {expense.date}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-orange-600 text-right">
                            {expense.expenseNumber}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {translateCategory(expense.category)}
                          </td>
                          <td className={`px-4 py-3 text-sm text-gray-700 text-right ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {translateDescription(expense.description)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-orange-600 text-right">
                            {parseFloat(expense.amount).toFixed(2)}
                          </td>
                          <td className={`px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right ${isUrdu ? 'font-urdu' : ''}`}>
                            {expense.paymentMethod}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${isUrdu ? 'font-urdu' : ''} ${expense.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {translateStatus(expense.status)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : filterExpensesData(expenses).length === 0 && expenses.length > 0 ? (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'فلٹرز کے مطابق کوئی نتیجہ نہیں ملا' : 'No results match the filters'}</p>
                  </div>
                ) : (
                  <div className={`p-8 text-gray-500 ${isUrdu ? 'text-right' : 'text-center'}`}>
                    <p className={isUrdu ? 'font-urdu' : ''}>{isUrdu ? 'کوئی خراجات نہیں ملی' : 'No expenses found'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        table {
          border-collapse: separate;
          border-spacing: 0;
        }

        table thead th {
          position: sticky;
          top: 0;
          z-index: 10;
          background-color: #f9fafb;
        }

        table tbody tr {
          transition: all 0.2s ease;
        }

        table tbody tr:hover {
          transform: scale(1.01);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          background-color: rgba(59, 130, 246, 0.05);
        }
      `}</style>
    </>
  );
}

export default RozNamacha;
